import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { askQuestion, getSessionMessages } from '../services/astroService';
import { setBalances, logout } from './userSlice';

const MAX_QUESTIONS_PER_SESSION = 5;

export const submitQuestion = createAsyncThunk(
  'question/submit',
  async (question, { getState, rejectWithValue, dispatch }) => {
    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    try {
      const { session, proMode } = getState().question;
      const start = Date.now();

      const response = await askQuestion(question, session.id, proMode);

      const elapsed = Date.now() - start;
      if (elapsed < 1000) {
        await delay(1000 - elapsed);
      }

      // Update balances in user state after successful question
      dispatch(setBalances(response));

      return response;
    } catch (err) {
      // Handle credit-related errors specially
      if (err.code === 'NO_CREDITS') {
        dispatch(setBalances({ credits: err.credits ?? 0, dailyFreeCredits: 0 }));
        return rejectWithValue({ message: err.message, code: 'NO_CREDITS', credits: err.credits });
      }
      if (err.code === 'INSUFFICIENT_CREDITS_FOR_PRO') {
        return rejectWithValue({ message: err.message, code: 'INSUFFICIENT_CREDITS_FOR_PRO' });
      }
      return rejectWithValue({ message: err.message });
    }
  }
);

export const loadSession = createAsyncThunk(
  'question/loadSession',
  async (sessionId) => {
    const qnas = await getSessionMessages(sessionId);
    return { sessionId, qnas };
  }
);

const createNewSession = () => ({
  id: nanoid(),
  questionCount: 0,
  createdAt: Date.now(),
});

const buildInitialState = () => ({
  question: '',
  answer: '',
  loading: false,
  error: null,
  noCredits: false,
  proMode: false,  // sticky toggle — Pro mode burns 2 credits per question
  pendingLandingQuestion: null, // Question from landing page to auto-submit
  session: createNewSession(),
  messages: [],
});

const questionSlice = createSlice({
  name: 'question',
  initialState: buildInitialState(),
  reducers: {
    setQuestion(state, action) {
      state.question = action.payload;
    },
    addMessage: {
      prepare(payload) {
        return {
          payload: {
            id: payload.id || nanoid(),
            role: payload.role || 'server',
            type: payload.type || 'text',
            status: payload.status || 'sent',
            createdAt: Date.now(),
            ...payload,
          },
        };
      },
      reducer(state, action) {
        state.messages.push(action.payload);
      },
    },
    updateMessage(state, action) {
      const { id, changes } = action.payload;
      const msg = state.messages.find((m) => m.id === id);
      if (msg) Object.assign(msg, changes);
    },
    removeMessage(state, action) {
      const id = action.payload;
      state.messages = state.messages.filter((m) => m.id !== id);
    },
    startNewSession(state) {
      const currentQuestion = state.question;
      state.session = createNewSession();
      state.messages = [];
      state.question = currentQuestion;
      state.answer = '';
      state.error = null;
      state.pendingLandingQuestion = null;
    },
    reset() {
      return buildInitialState();
    },
    clearNoCredits(state) {
      state.noCredits = false;
    },
    setProMode(state, action) {
      state.proMode = !!action.payload;
    },
    // Called once an answer has been moved into the messages array.
    // Without this, navigating away from /chat and coming back would re-process
    // the same answer (lastProcessedAnswer is local state and resets on remount).
    clearAnswer(state) {
      state.answer = '';
    },
    // Set pending question when navigating from landing page
    setPendingLandingQuestion(state, action) {
      state.pendingLandingQuestion = action.payload;
      state.question = ''; // Clear input, question moves to pending
    },
    clearPendingLandingQuestion(state) {
      state.pendingLandingQuestion = null;
    },
    restoreSession(state, action) {
      const { session, messages, question } = action.payload;
      if (session) {
        state.session = session;
      }
      if (messages) {
        state.messages = messages;
      }
      // Restore the question to input field (NOT to pendingLandingQuestion)
      // This means user must manually submit after payment
      state.question = question || '';
      state.pendingLandingQuestion = null; // Explicitly don't auto-submit
      state.answer = '';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.noCredits = false;
      })
      .addCase(submitQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.answer = action.payload.answer;
        // Only count valid questions that consumed a credit
        if (action.payload.creditConsumed) {
          state.session.questionCount += 1;
        }
      })
      .addCase(submitQuestion.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.code === 'NO_CREDITS') {
          state.noCredits = true;
          state.error = null;
        } else {
          state.error = action.payload?.message || action.payload;
        }
      })
      .addCase(loadSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadSession.fulfilled, (state, action) => {
        const { sessionId, qnas } = action.payload;
        state.loading = false;
        state.session = { id: sessionId, questionCount: qnas.length, createdAt: Date.now() };
        state.messages = [];
        qnas.forEach((qna) => {
          state.messages.push({
            id: nanoid(),
            role: 'user',
            type: 'text',
            status: 'sent',
            content: qna.question,
            createdAt: new Date(qna.time).getTime(),
          });
          state.messages.push({
            id: nanoid(),
            role: 'server',
            type: 'text',
            status: 'sent',
            content: qna.answer,
            feedback: qna.feedback || undefined,
            createdAt: new Date(qna.time).getTime(),
          });
        });
        state.question = '';
        state.answer = '';
        state.error = null;
      })
      .addCase(loadSession.rejected, (state) => {
        state.loading = false;
      })
      // Clear chat state whenever the user logs out (manual logout, token expiry, account deletion).
      // Otherwise stale messages from the previous user persist into the next session.
      .addCase(logout, () => buildInitialState());
  },
});

// Selectors
export const selectCanAskMore = (state) =>
  state.question.session.questionCount < MAX_QUESTIONS_PER_SESSION;

export const selectQuestionsRemaining = (state) =>
  MAX_QUESTIONS_PER_SESSION - state.question.session.questionCount;

export const selectEnabled = (state) =>
  state.question.question?.length > 0 && !state.question.loading;

export const {
  setQuestion,
  addMessage,
  updateMessage,
  removeMessage,
  startNewSession,
  reset,
  clearNoCredits,
  clearAnswer,
  setProMode,
  setPendingLandingQuestion,
  clearPendingLandingQuestion,
  restoreSession
} = questionSlice.actions;

export default questionSlice.reducer;
